import { Link, useLocation } from 'react-router-dom';
import { PenLine, BookOpen, Moon, Sun } from 'lucide-react';
import { LoginArea } from '@/components/auth/LoginArea';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/lib/utils';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { theme, setTheme } = useTheme();
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Navigation */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-sm">
        <div className="container flex h-16 items-center justify-between gap-4">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2.5 shrink-0 group"
          >
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
              <BookOpen className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-serif font-semibold text-lg text-foreground tracking-tight hidden sm:block">
              Hindsight Wisdom
            </span>
          </Link>

          {/* Nav links */}
          <nav className="flex items-center gap-1">
            <Link to="/">
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'font-medium',
                  location.pathname === '/' && 'bg-accent text-accent-foreground'
                )}
              >
                Browse
              </Button>
            </Link>
            <Link to="/write">
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'font-medium gap-1.5',
                  location.pathname === '/write' && 'bg-accent text-accent-foreground'
                )}
              >
                <PenLine className="w-4 h-4" />
                <span className="hidden sm:inline">Write</span>
              </Button>
            </Link>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              aria-label="Toggle theme"
              className="text-muted-foreground"
            >
              {theme === 'dark'
                ? <Sun className="w-4 h-4" />
                : <Moon className="w-4 h-4" />
              }
            </Button>
            <LoginArea className="max-w-48" />
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 mt-16">
        <div className="container flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            <span className="font-serif italic">Hindsight Wisdom</span>
            <span>— letters to our past selves</span>
          </div>
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground transition-colors"
          >
            Vibed with Shakespeare
          </a>
        </div>
      </footer>
    </div>
  );
}
